import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Leaderboard.css';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { MatrixElement, MatrixController } from 'chartjs-chart-matrix';
import 'chartjs-chart-matrix';
import Plot from 'react-plotly.js';

ChartJS.register(LineElement, PointElement, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, MatrixElement, MatrixController);

function Leaderboard(props) {
  const properCaps = { 'sora': 'Sora','runway': 'Runway', 'svd': 'SVD', 'veo': 'Veo', 'pika': 'Pika', 'genmo': 'Genmo', 'kling': 'Kling', 'luma': 'Luma', 'opensora': 'OpenSora', 'minimax': 'Minimax', }
  const excludeModels = ['veo']
  const baseURL = props.baseURL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    async function fetchData() {
      const response = await fetch(`${baseURL}/api/v1/leaderboard`, {
        method: 'GET'
      });
      const response_json = await response.json();
      const filteredData = response_json.filter(model => !excludeModels.includes(model.model.toLowerCase()));
      setData(filteredData);
      setData(filteredData);
      setLoading(false);
    }
    fetchData();
  }, [baseURL]);

  const today = new Date().toISOString().split('T')[0];
  const navigate = useNavigate();

  if (loading) {
    return <div className="Leaderboard-loading">Loading...</div>;
  }

  const LeaderboardInfo = () => {
    try {
      let totalVotes = 0;
      for (let i = 0; i < data.length; i++) {
        totalVotes += data[i].scores.win.total + data[i].scores.loss.total + data[i].scores.tie.total;
      }
      totalVotes = totalVotes / 2;
      return (
        <p className="Leaderboard-info">
          Total #models: {data.length} &nbsp;&nbsp;&nbsp; Total #votes: {totalVotes} &nbsp;&nbsp;&nbsp; Last updated: {today}
        </p>
      )
    }
    catch {
      return (<></>)
    }
  }

  const sortedData = [...data].sort((a, b) => {
    if (sortOrder === 'elo-asc') return a.scores.elo - b.scores.elo;
    if (sortOrder === 'elo-desc') return b.scores.elo - a.scores.elo;
    const votesA = a.scores.win.total + a.scores.loss.total + a.scores.tie.total;
    const votesB = b.scores.win.total + b.scores.loss.total + b.scores.tie.total;
    if (sortOrder === 'votes-asc') return votesA - votesB;
    if (sortOrder === 'votes-desc') return votesB - votesA;
    return 0;
  });

  const calculateRankUB = (model, data) => {
    return 1 + data.filter(otherModel => otherModel.scores.elo - otherModel.scores.ci_lower > model.scores.elo + model.scores.ci_upper).length;
  };


  const DataTable = () => {
    try {
      return (
        <table className="Leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Model</th>
              <th onClick={() => setSortOrder(sortOrder === 'elo-asc' ? 'elo-desc' : 'elo-asc')}>
                Elo Rating {sortOrder === 'elo-asc' ? '▲' : '▼'}
              </th>
              <th>95% CI</th>
              <th onClick={() => setSortOrder(sortOrder === 'votes-asc' ? 'votes-desc' : 'votes-asc')}>
                Votes {sortOrder === 'votes-asc' ? '▲' : '▼'}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((model, index) => (
              <tr key={properCaps[model.model]}>
                <td>
                  {calculateRankUB(model, data)}{" "}
                  {index === 0 && "🥇"}
                  {index === 1 && "🥈"}
                  {index === 2 && "🥉"}
                </td>
                <td>{properCaps[model.model] + " " + model.version}</td>
                <td>{Math.round(model.scores.elo)}</td>
                <td>{"+" + Math.round(model.scores.ci_upper) + "/-" + Math.round(model.scores.ci_lower)}</td>
                <td>{model.scores.win.total + model.scores.loss.total + model.scores.tie.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }
    catch {
      return (<></>)
    }
  }

  const ConfidenceIntervalChart = ({ data }) => {
    const chartData = {
      labels: data.map(model => `${properCaps[model.model]} ${model.version}`),
      datasets: [
        ...data.map(model => ({
          label: `${properCaps[model.model]} ${model.version} Elo`,
          data: [{ x: `${properCaps[model.model]} ${model.version}`, y: model.scores.elo }],
          borderColor: 'rgba(40,160,240,1)',
          backgroundColor: 'rgba(40,160,240,0.5)',
          borderWidth: 1,
          pointRadius: 2,
          pointHoverRadius: 3,
          fill: true
        })),
        ...data.map(model => ({
          label: `${properCaps[model.model]} ${model.version} CI`,
          data: [
            { x: `${properCaps[model.model]} ${model.version}`, y: model.scores.elo - model.scores.ci_lower }, // Lower CI
            { x: `${properCaps[model.model]} ${model.version}`, y: model.scores.elo + model.scores.ci_upper }  // Upper CI
          ],
          borderColor: 'rgba(40,160,240,1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
        })),
      ]
    };

    const chartOptions = {
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Figure 1: Confidence Intervals on Model Strength',
          font: {
            size: 16
          },
          color: 'rgb(240, 240, 240)',
          padding: {
            bottom: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              return `${label}: ${context.raw.y}`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Model',
            color: 'rgb(240, 240, 240)',
            font: {
              size: 14
            }
          },
          ticks: {
            color: 'rgb(220, 220, 220)'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Elo Rating',
            color: 'rgb(240, 240, 240)',
            font: {
              size: 14
            }
          },
          ticks: {
            color: 'rgb(220, 220, 220)',
          }
        }
      }
    };

    return <Line data={chartData} options={chartOptions} />;
  };

  const WinRateChart = ({ data }) => {
    const chartData = {
      labels: data.map(model => `${properCaps[model.model]} ${model.version}`),
      datasets: [
        {
          label: 'Average Win Rate (%)',
          data: data.map(model => {
            const totalWins = model.scores.win.total;
            const totalMatches = model.scores.win.total + model.scores.loss.total;
            return ((totalWins / totalMatches) * 100).toFixed(2);
          }),
          backgroundColor: 'rgba(40,160,240,0.5)',
          borderColor: 'rgba(40,160,240,1)',
          borderWidth: 1,
        }
      ]
    };

    const chartOptions = {
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Figure 2: Average Win Rate Against All Other Models (Excl. Ties)',
          font: {
            size: 16
          },
          color: 'rgb(240, 240, 240)',
          padding: {
            bottom: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              return `Win Rate: ${context.raw}%`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Model',
            color: 'rgb(240, 240, 240)',
            font: {
              size: 14
            }
          },
          ticks: {
            color: 'rgb(240, 240, 240)'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Average Win Rate (%)',
            color: 'rgb(240, 240, 240)',
            font: {
              size: 14
            }
          },
          ticks: {
            color: 'rgb(240, 240, 240)'
          }
        }
      }
    };

    return <Bar data={chartData} options={chartOptions} />;
  };

  function PairwiseWinRates({ data }) {
    //const models = data.map(model => model.model);
    const models = data.map(model => `${model.model} ${model.version}`);

    const heatmapData = models.map((modelA, i) =>
      models.map((modelB, j) => {
        if (modelA === modelB) return null;
        // const modelAName = modelA.split(" ")[0];
        // const modelBName = modelB.split(" ")[0];
        const modelAScores = data.find(d => `${d.model} ${d.version}` === modelA)?.scores;
        const winCount = modelAScores?.win[modelB] || 0;
        const lossCount = modelAScores?.loss[modelB] || 0;
        const totalMatches = winCount + lossCount;
        if (totalMatches === 0) return null;
        return ((winCount / totalMatches) * 100).toFixed(2);
      })
    );

    return (
      <div className="Leaderboard-graph">
        <h2 style={{ fontSize: '1em', marginBottom: '0px', textAlign: 'center', color: 'rgb(240, 240, 240)' }}>
          Figure 3: Average Win Rate for Top vs Bottom Model (Excl. Ties)
        </h2>
        <Plot
          data={[
            {
              z: heatmapData,
              x: models,
              y: models,
              type: 'heatmap',
              colorscale: [
                [0, 'rgb(50,0,0)'],
                [0.25, 'rgb(240,60,30)'],
                [0.5, 'rgb(240,140,60)'],
                [0.75, 'rgb(240,200,90)'],
                [1, 'rgb(255,255,200)'],
              ],
              text: heatmapData.map(row => row.map(value => value !== null ? `${value}%` : '')),
              texttemplate: '%{text}',
              colorbar: {
                title: {
                  text: 'Win Rate (%)',
                  font: {
                    color: 'rgb(240, 240, 240)'
                  }
                },
                tickfont: {
                  color: 'rgb(240, 240, 240)'
                }
              },
            }
          ]}
          layout={{
            width: 550,
            height: 500,
            autosize: true,
            aspectratio: {
              x: 1,
              y: 1
            },
            xaxis: {
              title: 'Bottom Model',
              color: 'rgb(240, 240, 240)',
              showgrid: false,
              automargin: true,
              tickfont: {
                size: 10
              }
            },
            yaxis: {
              title: {
                text: 'Top Model',
                standoff: 10,
              },
              color: 'rgb(240, 240, 240)',
              tickangle: 0,
              showgrid: false,
              automargin: true,
              tickfont: {
                size: 10
              }
            },
            paper_bgcolor: 'black',
            plot_bgcolor: 'black',
            margin: {
              l: 50,
              r: 50,
              t: 50,
              b: 50
            },
          }}
        />
      </div>
    );
  }

  function PairwiseBattleCounts({ data }) {
    //const models = data.map(model => model.model);
    const models = data.map(model => `${model.model} ${model.version}`);

    const heatmapData = models.map((modelA, i) =>
      models.map((modelB, j) => {
        if (modelA === modelB) return null;
        // const modelAName = modelA.split(" ")[0];
        // const modelBName = modelB.split(" ")[0];
        const modelAScores = data.find(d => `${d.model} ${d.version}` === modelA)?.scores;
        const winCount = modelAScores?.win[modelB] || 0;
        const lossCount = modelAScores?.loss[modelB] || 0;
        const totalMatches = winCount + lossCount;
        return totalMatches;
      })
    );

    return (
      <div className="Leaderboard-graph">
        <h2 style={{ fontSize: '1em', marginBottom: '0px', textAlign: 'center', color: 'rgb(240, 240, 240)' }}>
          Figure 4: Pairwise Battle Counts (Excl. Ties)
        </h2>
        <Plot
          data={[
            {
              z: heatmapData,
              x: models,
              y: models,
              type: 'heatmap',
              colorscale: [
                [0, 'rgb(50,0,0)'],
                [0.25, 'rgb(240,60,30)'],
                [0.5, 'rgb(240,140,60)'],
                [0.75, 'rgb(240,200,90)'],
                [1, 'rgb(255,255,200)'],
              ],
              text: heatmapData.map(row => row.map(value => value !== null ? `${value}` : '')),
              texttemplate: '%{text}',
              colorbar: {
                title: {
                  text: 'Battle Count',
                  font: {
                    color: 'rgb(240, 240, 240)'
                  }
                },
                tickfont: {
                  color: 'rgb(240, 240, 240)'
                }
              },
            }
          ]}
          layout={{
            width: 550,
            height: 500,
            autosize: true,
            aspectratio: {
              x: 1,
              y: 1
            },
            xaxis: {
              title: 'Bottom Model',
              color: 'rgb(240, 240, 240)',
              showgrid: false,
              automargin: true,
              tickfont: {
                size: 10
              }
            },
            yaxis: {
              title: {
                text: 'Top Model',
                standoff: 10,
              },
              color: 'rgb(240, 240, 240)',
              tickangle: 0,
              showgrid: false,
              automargin: true,
              tickfont: {
                size: 10
              }
            },
            paper_bgcolor: 'black',
            plot_bgcolor: 'black',
            margin: {
              l: 50,
              r: 50,
              t: 50,
              b: 50
            },
          }}
        />
      </div>
    );
  }

  return (
    <div id='Leaderboard' className="Leaderboard-body">
      <div className="Leaderboard-header">
        <h1 className="Leaderboard-title">🏆 Video Arena Leaderboard 🏅</h1>
        <button className="Leaderboard-vote-button" onClick={() => navigate('/')}>
          Vote! 🗳️
        </button>
      </div>

      <p>
        Welcome to Video Arena! Video Arena is a free service allowing users to access, compare, and rank text-to-video capabilities of state-of-the-art generative models. We’ve collected many human pairwise comparisons/votes and ranked the different models using the ELO ratings calculated from the Bradley-Terry model. Voting provides valuable data for both consumers selecting models and improving underlying models, so please contribute by casting your vote!<br /><br />Our leaderboard updates once a day! ⏳
      </p>
      {/* Our leaderboard updates in real time, so please be patient! ⏳ */}
      <LeaderboardInfo></LeaderboardInfo>

      <div className="Leaderboard-sort">
        <label htmlFor="sortOrder">Sort by: </label>
        <select id="sortOrder" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
          <option value="elo-desc">Elo Rating (High to Low)</option>
          <option value="elo-asc">Elo Rating (Low to High)</option>
          <option value="votes-desc">Votes (High to Low)</option>
          <option value="votes-asc">Votes (Low to High)</option>
        </select>
      </div>

      <div className="Leaderboard-table-container">
        <DataTable></DataTable>
      </div>
      <p className="Leaderboard-footer">
        *Rank (UB): model's ranking (upper-bound), defined by one + the number of models that are statistically better than the target model. Model A is statistically better than model B if A's lower-bound elo rating is greater than B's upper-bound elo rating, in terms of their 95% confidence intervals.
        <br /><br />
        Note: in the graphs below, we only include models with at least 100 votes to maintain a small confidence interval range.
      </p>

      <h2 className="Leaderboard-graph-title">Video Arena Battle Statistics and Charts 📊</h2>

      <div className="Leaderboard-graphs-container">
        <div className="Leaderboard-graph-row">
          <div className="Leaderboard-graph">
            <ConfidenceIntervalChart data={data} />
          </div>
          <div className="Leaderboard-graph">
            <WinRateChart data={data} />
          </div>
        </div>
      </div>
      <div className="heatmap-container">
        <div className="heatmap-row">
          <div className="heatmap-chart">
            <PairwiseWinRates data={data} />
          </div>
          <div className="heatmap-chart">
            <PairwiseBattleCounts data={data} />
          </div>
        </div>
      </div>

      <p>
        For more information on how we calculated our battle statistics, such as the Bradley-Terry model used for the MLE elo rating, check out <a href="https://colab.research.google.com/drive/1Szwfrlp94uETiiidUzgVEPCinwU7Gm3O?usp=sharing" target="_blank" rel="noopener noreferrer" style={{ color: 'rgb(100, 180, 240)' }}>this notebook</a>.
      </p>
    </div>
  );
}

export default Leaderboard;